import {
  Briefcase,
  CheckCheck,
  CreditCard,
  FileText,
  Gift,
  Link,
  MessageCircle,
  MoreHorizontal,
  ShoppingBag,
  Tag
} from "lucide-react";
import React, { useMemo } from "react";
import { BookingLabelsEnum } from "../../../../../../../types/bookingEnums";
import { WebViewTooltip } from "../../../../SubHeader/WebViewTooltip";

interface BookingCardIconsProps {
  booking: any;
  isOnRunSheet: boolean;
}

export const BookingCardIcons: React.FC<BookingCardIconsProps> = ({
  booking,
  isOnRunSheet
}) => {
  // Calculate maxVisibleIcons based on booking type length
  const maxVisibleIcons = useMemo(() => {
    const bookingType = booking.type;

    if (!bookingType?.name) {
      return 5; // Default when no booking type
    }

    const bookingTypeLength = bookingType.name.length;

    // Dynamic icon allocation based on booking type length
    if (bookingTypeLength <= 5) {
      return 6; // Short booking types (e.g., "Lunch") - show more icons
    } else if (bookingTypeLength <= 10) {
      return 5; // Medium booking types - show moderate icons
    } else if (bookingTypeLength <= 15) {
      return 4; // Longer booking types - show fewer icons
    } else {
      return 3; // Very long booking types (e.g., "Exclusive Hire Enquiry") - show minimal icons
    }
  }, [booking.type?.name, booking.id]);

  if (isOnRunSheet) {
    return null;
  }

  // Always show guest count first
  const guestCountElement =
    booking.actualGuests !== undefined && booking.actualGuests !== null ? (
      <>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="custom-user-icon"
          style={{
            fill: "none !important",
            strokeWidth: "2 !important",
            width: "14px !important",
            height: "14px !important"
          }}
        >
          <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
        <span className="numPeople" style={{ fontSize: "14px" }}>
          {booking.actualGuests}
        </span>
      </>
    ) : null;

  // Collect feature icons (excluding guest count)
  const featureIcons = [];

  if ((booking.labels?.length ?? 0) !== 0) {
    featureIcons.push({
      type: "labels",
      element: (
        <WebViewTooltip content="Has Labels" side="bottom">
          <Tag
            size={14}
            color="var(--primaryColor)"
            fill="none"
            strokeWidth={2}
          />
        </WebViewTooltip>
      )
    });
  }

  if (
    (booking.linkedBookings?.length ?? 0) !== 0 ||
    booking.labels?.find(
      (LinkedBooking: string) =>
        LinkedBooking === BookingLabelsEnum.LinkedBooking
    ) === BookingLabelsEnum.LinkedBooking
  ) {
    featureIcons.push({
      type: "linked",
      element: (
        <WebViewTooltip content="Linked Booking" side="bottom">
          <Link
            size={14}
            color="var(--primaryColor)"
            fill="none"
            strokeWidth={2}
          />
        </WebViewTooltip>
      )
    });
  }

  if (booking.company?.[0]) {
    featureIcons.push({
      type: "company",
      element: (
        <WebViewTooltip content="Company Booking" side="bottom">
          <Briefcase
            size={14}
            color="var(--primaryColor)"
            fill="none"
            strokeWidth={2}
          />
        </WebViewTooltip>
      )
    });
  }

  if (booking.doubleConfirmed) {
    featureIcons.push({
      type: "doubleConfirmed",
      element: (
        <WebViewTooltip content="Double Confirmed" side="bottom">
          <CheckCheck
            size={14}
            color="var(--primaryColor)"
            fill="none"
            strokeWidth={2}
          />
        </WebViewTooltip>
      )
    });
  }

  if ((booking.comments?.length ?? 0) !== 0) {
    featureIcons.push({
      type: "comments",
      element: (
        <WebViewTooltip content="Has Comments" side="bottom">
          <MessageCircle
            size={14}
            color="var(--primaryColor)"
            fill="none"
            strokeWidth={2}
          />
        </WebViewTooltip>
      )
    });
  }

  if (booking.hasPreorders) {
    featureIcons.push({
      type: "preorders",
      element: (
        <WebViewTooltip content="Has Pre-orders" side="bottom">
          <ShoppingBag
            size={14}
            color="var(--primaryColor)"
            fill="none"
            strokeWidth={2}
          />
        </WebViewTooltip>
      )
    });
  }

  if ((booking.deposits?.length ?? 0) !== 0) {
    featureIcons.push({
      type: "deposits",
      element: (
        <WebViewTooltip content="Has Deposit" side="bottom">
          <CreditCard
            size={14}
            color="var(--primaryColor)"
            fill="none"
            strokeWidth={2}
          />
        </WebViewTooltip>
      )
    });
  }

  if (booking.notes || booking.offer?.id) {
    featureIcons.push({
      type: "notes",
      element: (
        <WebViewTooltip content="Has Notes or Offer" side="bottom">
          <FileText
            size={14}
            color="var(--primaryColor)"
            fill="none"
            strokeWidth={2}
          />
        </WebViewTooltip>
      )
    });
  }

  if (booking.labels?.find((label: string) => label === "VIP")) {
    featureIcons.push({
      type: "vip",
      element: (
        <WebViewTooltip content="VIP Customer" side="bottom">
          <Gift
            size={14}
            color="var(--primaryColor)"
            fill="none"
            strokeWidth={2}
          />
        </WebViewTooltip>
      )
    });
  }

  // Limit visible feature icons (guest count is always shown)
  const visibleFeatureIcons = featureIcons.slice(0, maxVisibleIcons - 1); // -1 for guest count
  const hiddenFeatureIcons = featureIcons.slice(maxVisibleIcons - 1);

  return (
    <>
      {/* Always show guest count first */}
      {guestCountElement}

      {/* Show visible feature icons */}
      {visibleFeatureIcons.map((icon, index) => (
        <React.Fragment key={`${icon.type}-${index}`}>
          {icon.element}
        </React.Fragment>
      ))}

      {/* Show more icon if there are hidden feature icons */}
      {hiddenFeatureIcons.length > 0 && (
        <WebViewTooltip
          content={
            <div>
              {hiddenFeatureIcons.map((icon, index) => (
                <div key={`${icon.type}-${index}`}>
                  {icon.type === "labels" && "Has Labels"}
                  {icon.type === "linked" && "Linked Booking"}
                  {icon.type === "company" && "Company Booking"}
                  {icon.type === "doubleConfirmed" && "Double Confirmed"}
                  {icon.type === "comments" && "Has Comments"}
                  {icon.type === "preorders" && "Has Pre-orders"}
                  {icon.type === "deposits" && "Has Deposit"}
                  {icon.type === "notes" && "Has Notes or Offer"}
                  {icon.type === "vip" && "VIP Customer"}
                </div>
              ))}
            </div>
          }
          side="bottom"
        >
          <MoreHorizontal size={16} color="var(--primaryColor)" />
        </WebViewTooltip>
      )}
    </>
  );
};
